import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Percent, Crown, Users as UsersIcon, CreditCard, Flag, Grid3x3,
  Search as SearchIcon, FileText, Settings, LogOut, Save, Plus, Trash2, RefreshCw, Bell, Check, X, Sun, Moon, Loader2, Upload
} from "lucide-react";
import api, { apiError, fmtTL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";

const TABS = [
  { k: "dash", label: "Özet", Icon: LayoutDashboard },
  { k: "pricing", label: "Fiyatlandırma", Icon: Percent },
  { k: "payments", label: "Ödemeler", Icon: CreditCard },
  { k: "ranks", label: "Rank & Ortaklık", Icon: Crown },
  { k: "countries", label: "Ülke Etiketleri", Icon: Flag },
  { k: "services", label: "Servisler", Icon: Grid3x3 },
  { k: "seo", label: "SEO", Icon: SearchIcon },
  { k: "blog", label: "Blog", Icon: FileText },
  { k: "settings", label: "Ayarlar", Icon: Settings },
];

function Field({ label, children }) {
  return <div><label className="text-sm font-medium block mb-1.5">{label}</label>{children}</div>;
}
const inputCls = "pinput w-full rounded-xl h-11 px-3 outline-none focus:border-brand-500";

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("dash");
  const [s, setS] = useState(null);
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [countries, setCountries] = useState([]);
  const [services, setServices] = useState([]);
  const [posts, setPosts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [cq, setCq] = useState("");

  useEffect(() => { if (user === false) nav("/giris"); else if (user && user.role !== "admin") nav("/panel/satin-al"); }, [user, nav]);
  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setS(data)).catch(() => {});
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (tab === "payments") api.get("/admin/payments").then(({ data }) => setPayments(data.payments || []));
    if (tab === "countries" && countries.length === 0) api.get("/admin/countries").then(({ data }) => setCountries(data.countries || []));
    if (tab === "services" && services.length === 0) api.get("/admin/services").then(({ data }) => setServices(data.services || []));
    if (tab === "blog") api.get("/admin/blog").then(({ data }) => setPosts(data.posts || []));
  }, [tab]);

  const save = async (patch) => {
    setSaving(true);
    try { const { data } = await api.put("/admin/settings", patch); setS(data); toast.success("Kaydedildi"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="min-h-screen bg-panel-bg grid place-items-center text-panel-muted">Yükleniyor...</div>;

  return (
    <div className="min-h-screen bg-panel-bg text-panel-text">
      <header className="sticky top-0 z-40 bg-panel-header border-b border-panel-border">
        <div className="mx-auto max-w-6xl px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3"><Logo size="sm" /><span className="text-xs font-bold bg-brand/15 text-brand-500 rounded-full px-2.5 py-1">ADMIN</span></div>
          <div className="flex items-center gap-2">
            <button onClick={() => nav("/panel/satin-al")} className="rounded-xl card h-10 px-3 text-sm font-semibold">Panel</button>
            <button onClick={() => { logout(); nav("/"); }} className="h-10 w-10 grid place-items-center rounded-xl card"><LogOut size={16} /></button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-6 grid md:grid-cols-[220px_1fr] gap-6">
        {/* sidebar */}
        <aside className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible">
          {TABS.map(({ k, label, Icon }) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`flex items-center gap-2.5 rounded-xl px-3.5 h-11 text-sm font-medium whitespace-nowrap transition-colors ${tab === k ? "btn-blue" : "card hover:border-brand-500"}`}>
              <Icon size={17} /> {label}
            </button>
          ))}
        </aside>

        <main className="min-w-0">
          {/* DASH */}
          {tab === "dash" && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[["Kullanıcı", stats?.users], ["Aktivasyon", stats?.activations], ["Bekleyen Ödeme", stats?.pending_payments],
                ["Toplam Yükleme", fmtTL(stats?.total_deposits || 0)], ["Toplam Bakiye", fmtTL(stats?.total_balance || 0)], ["Sağlayıcı Bakiye", stats?.hero_balance]].map(([l, v]) => (
                <div key={l} className="card p-5"><div className="text-xs text-panel-muted">{l}</div><div className="font-head text-2xl font-extrabold mt-1">{v ?? "-"}</div></div>
              ))}
            </div>
          )}

          {/* PRICING */}
          {tab === "pricing" && (
            <div className="card p-6 space-y-5 max-w-lg">
              <h2 className="font-head text-xl font-bold">Fiyatlandırma</h2>
              <Field label={`Toplu Kâr Marjı (%) — tüm servislere uygulanır`}>
                <input type="number" defaultValue={s.markup_percent} className={inputCls} id="markup" data-testid="admin-markup" />
                <p className="text-xs text-panel-muted mt-1">Örn: 60 = sağlayıcı maliyetine %60 eklenir.</p>
              </Field>
              <Field label="USD / TRY Kuru">
                <div className="flex gap-2">
                  <input type="number" defaultValue={s.usd_try_rate} className={inputCls} id="rate" data-testid="admin-rate" />
                  <button onClick={async () => { const { data } = await api.post("/admin/refresh-rate"); setS({ ...s, usd_try_rate: data.usd_try_rate }); document.getElementById("rate").value = data.usd_try_rate; toast.success("Kur güncellendi: " + data.usd_try_rate); }}
                    className="btn-blue rounded-xl px-3 shrink-0" title="Otomatik çek"><RefreshCw size={16} /></button>
                </div>
              </Field>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" defaultChecked={s.rate_auto} id="rateauto" /> Kuru saatlik otomatik güncelle (cron)</label>
              <button onClick={() => save({ markup_percent: Number(document.getElementById("markup").value), usd_try_rate: Number(document.getElementById("rate").value), rate_auto: document.getElementById("rateauto").checked })}
                disabled={saving} className="btn-blue rounded-xl h-11 px-5 font-semibold flex items-center gap-2" data-testid="admin-save-pricing"><Save size={16} /> Kaydet</button>
            </div>
          )}

          {/* PAYMENTS */}
          {tab === "payments" && (
            <div className="space-y-3">
              <h2 className="font-head text-xl font-bold">Bekleyen Ödemeler</h2>
              {payments.map((p) => (
                <div key={p.id} className="card p-4 flex items-center justify-between gap-3" data-testid={`admin-payment-${p.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold">{fmtTL(p.amount)} · {p.method === "iban" ? "Havale" : "Shopier"}</div>
                    <div className="text-xs text-panel-muted truncate">{p.user_email} · {p.ref_code || ""} · {(p.created_at || "").slice(0, 16).replace("T", " ")}</div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <span className={`text-xs font-semibold rounded-full px-2.5 py-1 self-center ${p.status === "waiting_confirm" ? "bg-amber-500/15 text-amber-500" : "bg-panel-input text-panel-muted"}`}>{p.status === "waiting_confirm" ? "Ödendi dedi" : "Bekliyor"}</span>
                    <button onClick={async () => { await api.post(`/admin/payments/${p.id}/approve`); toast.success("Onaylandı, bakiye yüklendi"); setPayments(payments.filter((x) => x.id !== p.id)); }}
                      data-testid={`approve-${p.id}`} className="h-10 w-10 grid place-items-center rounded-xl bg-emerald-500/15 text-emerald-500"><Check size={18} /></button>
                    <button onClick={async () => { await api.post(`/admin/payments/${p.id}/reject`); setPayments(payments.filter((x) => x.id !== p.id)); }}
                      className="h-10 w-10 grid place-items-center rounded-xl bg-red-500/15 text-red-500"><X size={18} /></button>
                  </div>
                </div>
              ))}
              {payments.length === 0 && <div className="text-center text-panel-muted py-10 text-sm">Bekleyen ödeme yok.</div>}
            </div>
          )}

          {/* RANKS */}
          {tab === "ranks" && (
            <div className="card p-6 space-y-5 max-w-xl">
              <h2 className="font-head text-xl font-bold">Rank Seviyeleri</h2>
              {s.rank_tiers.map((t, i) => (
                <div key={i} className="grid grid-cols-4 gap-2 items-end">
                  <Field label="İsim"><input defaultValue={t.name} className={inputCls} onChange={(e) => (s.rank_tiers[i].name = e.target.value)} /></Field>
                  <Field label="Min Yükleme ₺"><input type="number" defaultValue={t.min} className={inputCls} onChange={(e) => (s.rank_tiers[i].min = Number(e.target.value))} /></Field>
                  <Field label="İndirim %"><input type="number" defaultValue={t.discount} className={inputCls} onChange={(e) => (s.rank_tiers[i].discount = Number(e.target.value))} /></Field>
                  <Field label="Bonus ₺"><input type="number" defaultValue={t.bonus} className={inputCls} onChange={(e) => (s.rank_tiers[i].bonus = Number(e.target.value))} /></Field>
                </div>
              ))}
              <Field label="Ortaklık Komisyonu (%)"><input type="number" defaultValue={s.affiliate_percent} id="aff" className={inputCls} data-testid="admin-affiliate" /></Field>
              <button onClick={() => save({ rank_tiers: s.rank_tiers, affiliate_percent: Number(document.getElementById("aff").value) })}
                disabled={saving} className="btn-blue rounded-xl h-11 px-5 font-semibold flex items-center gap-2" data-testid="admin-save-ranks"><Save size={16} /> Kaydet</button>
            </div>
          )}

          {/* COUNTRIES */}
          {tab === "countries" && (
            <div>
              <h2 className="font-head text-xl font-bold mb-3">Ülke Etiketleri</h2>
              <input value={cq} onChange={(e) => setCq(e.target.value)} placeholder="Ülke ara..." className={inputCls + " mb-3"} />
              <p className="text-xs text-panel-muted mb-3">Popüler ve Yüksek Başarı etiketlerini işaretle, sonra Kaydet.</p>
              <div className="space-y-2 max-h-[55vh] overflow-auto">
                {countries.filter((c) => c.name.toLowerCase().includes(cq.toLowerCase())).slice(0, 80).map((c) => (
                  <div key={c.id} className="card p-3 flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-2"><img src={c.flag} className="h-4 w-6 rounded-sm object-cover" alt="" onError={(e) => (e.target.style.visibility = "hidden")} />{c.name}</span>
                    <div className="flex gap-4 text-xs">
                      <label className="flex items-center gap-1.5"><input type="checkbox" defaultChecked={c.popular} onChange={(e) => (c.popular = e.target.checked)} /> Popüler</label>
                      <label className="flex items-center gap-1.5"><input type="checkbox" defaultChecked={c.high_success} onChange={(e) => (c.high_success = e.target.checked)} /> Yüksek Başarı</label>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => { const tags = {}; countries.forEach((c) => { if (c.popular || c.high_success) tags[c.id] = { popular: !!c.popular, high_success: !!c.high_success }; }); save({ country_tags: tags }); }}
                disabled={saving} className="btn-blue rounded-xl h-11 px-5 font-semibold flex items-center gap-2 mt-4" data-testid="admin-save-countries"><Save size={16} /> Etiketleri Kaydet</button>
            </div>
          )}

          {/* SERVICES */}
          {tab === "services" && (
            <div>
              <h2 className="font-head text-xl font-bold mb-3">Servis Ayarları</h2>
              <p className="text-xs text-panel-muted mb-3">Servisi gizle veya Popüler olarak işaretle.</p>
              <div className="space-y-2 max-h-[60vh] overflow-auto">
                {services.map((sv) => (
                  <div key={sv.code} className="card p-3 flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-2"><img src={sv.icon} className="h-7 w-7 rounded-lg bg-white p-0.5 object-contain" alt="" onError={(e) => (e.target.style.visibility = "hidden")} />{sv.name}</span>
                    <div className="flex gap-4 text-xs">
                      <label className="flex items-center gap-1.5"><input type="checkbox" defaultChecked={sv.popular} onChange={(e) => (sv.popular = e.target.checked)} /> Popüler</label>
                      <label className="flex items-center gap-1.5"><input type="checkbox" defaultChecked={sv.hidden} onChange={(e) => (sv.hidden = e.target.checked)} /> Gizle</label>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => { const ov = {}; services.forEach((sv) => { if (sv.popular || sv.hidden) ov[sv.code] = { popular: !!sv.popular, hidden: !!sv.hidden }; }); save({ service_overrides: ov }); }}
                disabled={saving} className="btn-blue rounded-xl h-11 px-5 font-semibold flex items-center gap-2 mt-4"><Save size={16} /> Kaydet</button>
            </div>
          )}

          {/* SEO */}
          {tab === "seo" && (
            <div className="card p-6 space-y-4 max-w-2xl">
              <h2 className="font-head text-xl font-bold">SEO Ayarları</h2>
              <Field label="Site Başlığı (title)"><input defaultValue={s.seo?.title} id="seotitle" className={inputCls} /></Field>
              <Field label="Açıklama (description)"><textarea defaultValue={s.seo?.description} id="seodesc" className="pinput w-full rounded-xl p-3 outline-none focus:border-brand-500 min-h-[70px]" /></Field>
              <Field label="Anahtar Kelimeler (virgülle)"><textarea defaultValue={(s.seo?.keywords || []).join(", ")} id="seokw" className="pinput w-full rounded-xl p-3 outline-none min-h-[70px]" /></Field>
              <Field label="Ana Sayfa Alt SEO Metni (HTML)"><textarea defaultValue={s.seo?.homepage_seo_html} id="seohtml" className="pinput w-full rounded-xl p-3 outline-none font-mono text-xs min-h-[140px]" /></Field>
              <button onClick={() => save({ seo: { ...s.seo, title: document.getElementById("seotitle").value, description: document.getElementById("seodesc").value, keywords: document.getElementById("seokw").value.split(",").map((x) => x.trim()).filter(Boolean), homepage_seo_html: document.getElementById("seohtml").value } })}
                disabled={saving} className="btn-blue rounded-xl h-11 px-5 font-semibold flex items-center gap-2" data-testid="admin-save-seo"><Save size={16} /> Kaydet</button>
              <button onClick={async () => { const { data } = await api.post("/admin/notify-google"); toast.success("Google & Bing bilgilendirildi"); }}
                className="rounded-xl card h-11 px-5 font-semibold flex items-center gap-2 border border-panel-border" data-testid="admin-notify-google"><Bell size={16} /> Google'a Bildir (Sitemap)</button>
              <p className="text-xs text-panel-muted">Sitemap: <a href={`${process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml`} target="_blank" rel="noreferrer" className="text-brand-500">/api/sitemap.xml</a></p>
            </div>
          )}

          {/* BLOG */}
          {tab === "blog" && <BlogEditor posts={posts} setPosts={setPosts} />}

          {/* SETTINGS */}
          {tab === "settings" && (
            <div className="space-y-5 max-w-2xl">
              <div className="card p-6 space-y-4">
                <h2 className="font-head text-xl font-bold">IBAN / Havale Bilgileri</h2>
                {s.ibans.map((b, i) => (
                  <div key={i} className="grid grid-cols-3 gap-2">
                    <input defaultValue={b.bank} placeholder="Banka" className={inputCls} onChange={(e) => (s.ibans[i].bank = e.target.value)} />
                    <input defaultValue={b.iban} placeholder="IBAN" className={inputCls} onChange={(e) => (s.ibans[i].iban = e.target.value)} />
                    <div className="flex gap-1">
                      <input defaultValue={b.name} placeholder="Ad Soyad" className={inputCls} onChange={(e) => (s.ibans[i].name = e.target.value)} />
                      <button onClick={() => { s.ibans.splice(i, 1); setS({ ...s }); }} className="h-11 w-11 grid place-items-center rounded-xl bg-red-500/15 text-red-500 shrink-0"><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
                <button onClick={() => { s.ibans.push({ bank: "", iban: "", name: "" }); setS({ ...s }); }} className="text-sm text-brand-500 flex items-center gap-1"><Plus size={15} /> IBAN ekle</button>
                <button onClick={() => save({ ibans: s.ibans })} disabled={saving} className="btn-blue rounded-xl h-11 px-5 font-semibold flex items-center gap-2" data-testid="admin-save-ibans"><Save size={16} /> IBAN Kaydet</button>
              </div>

              <div className="card p-6 space-y-4">
                <h2 className="font-head text-xl font-bold">Shopier</h2>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" defaultChecked={s.shopier_enabled} id="shopen" /> Shopier ödemeyi aktif et</label>
                <Field label="Ürün Görseli (Shopier dijital ürün için)">
                  <ImageUpload current={s.product_image_url} onDone={(url) => setS({ ...s, product_image_url: url })} />
                </Field>
                <button onClick={() => save({ shopier_enabled: document.getElementById("shopen").checked, product_image_url: s.product_image_url })} disabled={saving} className="btn-blue rounded-xl h-11 px-5 font-semibold flex items-center gap-2"><Save size={16} /> Shopier Kaydet</button>
              </div>

              <div className="card p-6 space-y-4">
                <h2 className="font-head text-xl font-bold">Görünüm</h2>
                <Field label="Varsayılan Tema (yeni ziyaretçiler için)">
                  <div className="flex gap-2">
                    {["dark", "light"].map((t) => (
                      <button key={t} onClick={() => save({ theme_default: t })} className={`rounded-xl px-5 h-11 font-semibold flex items-center gap-2 border ${s.theme_default === t ? "btn-blue border-transparent" : "card"}`} data-testid={`theme-default-${t}`}>
                        {t === "dark" ? <Moon size={16} /> : <Sun size={16} />} {t === "dark" ? "Karanlık" : "Aydınlık"}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Ödeme Penceresi (dakika)"><input type="number" defaultValue={s.payment_window_min} id="pw" className={inputCls} onBlur={(e) => save({ payment_window_min: Number(e.target.value) })} /></Field>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function ImageUpload({ current, onDone }) {
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState(current);
  const up = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setUrl(data.url); onDone(data.url); toast.success("Yüklendi"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3">
      {url && <img src={url} alt="" className="h-16 w-16 rounded-xl object-cover border border-panel-border" />}
      <label className="rounded-xl card h-11 px-4 flex items-center gap-2 cursor-pointer text-sm font-semibold border border-panel-border">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Görsel Seç
        <input type="file" accept="image/*" hidden onChange={(e) => up(e.target.files[0])} data-testid="admin-product-image" />
      </label>
    </div>
  );
}

function BlogEditor({ posts, setPosts }) {
  const [form, setForm] = useState({ slug: "", title: "", excerpt: "", cover: "", html: "", published: true });
  const save = async () => {
    if (!form.slug || !form.title) return toast.error("Slug ve başlık gerekli");
    try { await api.post("/admin/blog", form); toast.success("Yazı kaydedildi"); const { data } = await api.get("/admin/blog"); setPosts(data.posts || []); setForm({ slug: "", title: "", excerpt: "", cover: "", html: "", published: true }); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="card p-6 space-y-3">
        <h2 className="font-head text-xl font-bold">Yeni / Düzenle</h2>
        <input placeholder="slug (örn: whatsapp-sms-onay)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className={inputCls} data-testid="blog-slug" />
        <input placeholder="Başlık" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="blog-title" />
        <input placeholder="Özet" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className={inputCls} />
        <input placeholder="Kapak görseli URL" value={form.cover} onChange={(e) => setForm({ ...form, cover: e.target.value })} className={inputCls} />
        <textarea placeholder="<h2>HTML içerik...</h2>" value={form.html} onChange={(e) => setForm({ ...form, html: e.target.value })} className="pinput w-full rounded-xl p-3 outline-none font-mono text-xs min-h-[200px]" data-testid="blog-html" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Yayında</label>
        <button onClick={save} className="btn-blue rounded-xl h-11 px-5 font-semibold flex items-center gap-2" data-testid="blog-save"><Save size={16} /> Kaydet</button>
      </div>
      <div className="space-y-2">
        <h2 className="font-head text-xl font-bold mb-2">Yazılar</h2>
        {posts.map((p) => (
          <div key={p.slug} className="card p-4 flex items-center justify-between">
            <div><div className="font-semibold text-sm">{p.title}</div><div className="text-xs text-panel-muted">/{p.slug} · {p.published ? "Yayında" : "Taslak"}</div></div>
            <div className="flex gap-2">
              <button onClick={() => setForm(p)} className="h-9 w-9 grid place-items-center rounded-lg card">✎</button>
              <button onClick={async () => { await api.delete(`/admin/blog/${p.slug}`); setPosts(posts.filter((x) => x.slug !== p.slug)); }} className="h-9 w-9 grid place-items-center rounded-lg bg-red-500/15 text-red-500"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {posts.length === 0 && <div className="text-panel-muted text-sm py-8 text-center">Yazı yok.</div>}
      </div>
    </div>
  );
}
